import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Copy } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRIES, CARGO_TYPES } from "@/constants/options";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  shipper_type: "business",
  full_name: "",
  company_name: "",
  email: "",
  phone: "",
  origin_country: "",
  destination_country: "",
  cargo_type: "",
  estimated_shipment_date: "",
  additional_notes: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FIELD_LABELS = {
  full_name: "Full Name",
  company_name: "Company Name",
  email: "Email",
  phone: "Phone",
  origin_country: "Origin Country",
  destination_country: "Destination Country",
  cargo_type: "Cargo Type",
  estimated_shipment_date: "Estimated Shipment Date",
};

function validateQuoteForm(form) {
  const required = [
    "full_name", "email", "phone", "origin_country",
    "destination_country", "cargo_type", "estimated_shipment_date",
  ];
  if (form.shipper_type === "business") required.splice(1, 0, "company_name");

  for (const field of required) {
    if (!String(form[field] || "").trim()) {
      return `${FIELD_LABELS[field]} is required.`;
    }
  }
  if (!EMAIL_RE.test(form.email.trim())) {
    return "Email: please enter a valid email address.";
  }
  return null;
}

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState(null);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  useEffect(() => {
    const handlePrefill = (e) => {
      const detail = e.detail || {};
      setForm((f) => ({ ...f, ...detail }));
      toast.success("Quote form pre-filled from your chat with the Compliance Assistant");
    };
    window.addEventListener("fergusons:prefill-quote", handlePrefill);
    return () => window.removeEventListener("fergusons:prefill-quote", handlePrefill);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateQuoteForm(form);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiClient.post("/quote-requests", form);
      toast.success("Quote request received. Our team will reach out shortly.");
      setReferenceNumber(data.reference_number);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyReference = () => {
    navigator.clipboard.writeText(referenceNumber);
    toast.success("Reference number copied");
  };

  return (
    <section id="quote" className="py-20 lg:py-28 bg-cream" data-testid="quote-form-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-10">
        <Reveal>
          <p className="label-eyebrow text-gold-muted mb-3">Freight Quote</p>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-ink">
            Request a Freight Quote
          </h2>
          <p className="mt-3 text-base text-ink-soft max-w-xl">
            Moving goods for your business, or shipping something personal? Tell us what and where —
            we'll quote it fast.
          </p>
        </Reveal>

        {referenceNumber && (
          <Reveal>
            <div
              data-testid="quote-reference-confirmation"
              className="mt-8 rounded-xl border border-gold/30 bg-gold/10 p-5 flex items-center justify-between gap-4 flex-wrap"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-gold-muted shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-ink">
                    Request received! Save your reference number to track this quote:
                  </p>
                  <p className="font-mono font-semibold text-lg text-ink mt-1" data-testid="quote-reference-number">
                    {referenceNumber}
                  </p>
                </div>
              </div>
              <button
                onClick={copyReference}
                data-testid="quote-reference-copy-btn"
                className="flex items-center gap-1.5 text-sm font-medium text-gold-muted hover:text-gold border border-gold/30 hover:border-gold/60 rounded-full px-4 py-2 transition-colors"
              >
                <Copy size={14} /> Copy
              </button>
            </div>
          </Reveal>
        )}

        <Reveal delay={0.1}>
          <form
            onSubmit={handleSubmit}
            noValidate
            data-testid="quote-request-form"
            className="mt-10 rounded-2xl border border-gold/20 bg-white shadow-sm p-6 sm:p-10 text-ink"
          >
            <div className="mb-8">
              <Label className="text-sm text-ink-soft mb-3 block">I am shipping as a</Label>
              <div className="flex gap-3">
                {[
                  { id: "business", label: "Business" },
                  { id: "individual", label: "Individual" },
                ].map((opt) => (
                  <button
                    type="button"
                    key={opt.id}
                    data-testid={`shipper-type-${opt.id}`}
                    onClick={() => set("shipper_type", opt.id)}
                    className={`px-6 py-2.5 rounded-full text-sm font-display font-semibold border transition-colors ${
                      form.shipper_type === opt.id
                        ? "bg-gold text-[#0A140F] border-gold"
                        : "border-ink/20 text-ink-soft hover:border-gold/60"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  required
                  data-testid="quote-input-full-name"
                  value={form.full_name}
                  onChange={(e) => set("full_name", e.target.value)}
                  className="mt-1.5 bg-cream border-ink/15 text-ink placeholder:text-ink-faint"
                />
              </div>
              <div>
                <Label htmlFor="company_name">
                  Company Name {form.shipper_type === "individual" && <span className="text-ink-faint">(if applicable)</span>}
                </Label>
                <Input
                  id="company_name"
                  required={form.shipper_type === "business"}
                  data-testid="quote-input-company-name"
                  value={form.company_name}
                  onChange={(e) => set("company_name", e.target.value)}
                  className="mt-1.5 bg-cream border-ink/15 text-ink placeholder:text-ink-faint"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  data-testid="quote-input-email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className="mt-1.5 bg-cream border-ink/15 text-ink placeholder:text-ink-faint"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  required
                  data-testid="quote-input-phone"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className="mt-1.5 bg-cream border-ink/15 text-ink placeholder:text-ink-faint"
                />
              </div>
              <div>
                <Label>Origin Country</Label>
                <Select value={form.origin_country} onValueChange={(v) => set("origin_country", v)}>
                  <SelectTrigger data-testid="quote-select-origin-country" className="mt-1.5 bg-cream border-ink/15">
                    <SelectValue placeholder="Select origin country" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Destination Country</Label>
                <Select value={form.destination_country} onValueChange={(v) => set("destination_country", v)}>
                  <SelectTrigger data-testid="quote-select-destination-country" className="mt-1.5 bg-cream border-ink/15">
                    <SelectValue placeholder="Select destination country" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cargo Type</Label>
                <Select value={form.cargo_type} onValueChange={(v) => set("cargo_type", v)}>
                  <SelectTrigger data-testid="quote-select-cargo-type" className="mt-1.5 bg-cream border-ink/15">
                    <SelectValue placeholder="Select cargo type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CARGO_TYPES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="estimated_shipment_date">Estimated Shipment Date</Label>
                <Input
                  id="estimated_shipment_date"
                  type="date"
                  required
                  data-testid="quote-input-shipment-date"
                  value={form.estimated_shipment_date}
                  onChange={(e) => set("estimated_shipment_date", e.target.value)}
                  className="mt-1.5 bg-cream border-ink/15 text-ink placeholder:text-ink-faint"
                />
              </div>
            </div>

            <div className="mt-5">
              <Label htmlFor="additional_notes">Additional Notes</Label>
              <Textarea
                id="additional_notes"
                rows={4}
                data-testid="quote-textarea-notes"
                value={form.additional_notes}
                onChange={(e) => set("additional_notes", e.target.value)}
                className="mt-1.5 bg-cream border-ink/15 text-ink placeholder:text-ink-faint"
                placeholder="Anything we should know about this shipment?"
              />
            </div>

            <motion.button
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              data-testid="quote-submit-btn"
              className="mt-7 w-full sm:w-auto bg-gold hover:bg-gold-hover disabled:opacity-60 text-[#0A140F] font-display font-semibold text-sm sm:text-base px-8 py-3.5 rounded-full flex items-center justify-center gap-2 transition-colors"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              Request a Freight Quote
            </motion.button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
